import { v4 as uuidv4 } from 'uuid'

// 로그인 오류 신고 시 함께 전송할 웹 환경 정보 수집
// 비밀번호·토큰·요청 본문은 어떤 경우에도 수집하지 않는다.

const MAX_ITEMS = 10
const SERVER_CHECK_TIMEOUT_MS = 3000
const SOFTWARE_RENDERER_PATTERN = /swiftshader|llvmpipe|software|basic render/i

const LOGIN_RESULT_MESSAGES = {
  '-1': '아이디 또는 비밀번호 오류',
  '-2': '아이디 또는 비밀번호 오류',
  '-3': '계약 만료 계정',
  '-4': '서버 점검 중',
  no_response: '서버 응답 없음',
  success: '성공'
}

const sessionId = uuidv4()
const jsErrors = []
const failedRequests = []
const shownMessages = []
const loginAttempt = {
  method: '',
  id: '',
  count: 0,
  firstAt: '',
  lastAt: '',
  lastResult: '',
  lastResultMessage: '',
  // 마지막으로 완료된 로그인 단계 (어디서 멈췄는지 확인용)
  stage: '',
  stageAt: ''
}
const webglLoad = {
  lastProgress: null,
  lastProgressAt: '',
  completedAt: '',
  durationMs: null
}
let webglInfoCache = null

function now() {
  return new Date().toISOString()
}

function push(list, item) {
  list.push(item)
  if (list.length > MAX_ITEMS) list.shift()
}

function stripQuery(url) {
  return (url || '').split('?')[0]
}

function initEnvCollector(axios) {
  window.addEventListener('error', (event) => {
    // 앱 내부 안내 팝업은 window "error" CustomEvent로 띄우므로 화면 표시 문구로 따로 기록
    if (event instanceof CustomEvent) {
      const text = event.detail && event.detail.errortext
      if (text) push(shownMessages, { message: text, at: now() })
      return
    }
    if (!(event instanceof ErrorEvent)) return
    push(jsErrors, {
      message: event.message,
      source: `${stripQuery(event.filename)}:${event.lineno}:${event.colno}`,
      stack: event.error && event.error.stack
        ? event.error.stack.split('\n').slice(0, 5).join('\n')
        : null,
      at: now()
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    push(jsErrors, {
      message: String((reason && reason.message) || reason),
      source: 'unhandledrejection',
      stack: reason && reason.stack
        ? reason.stack.split('\n').slice(0, 5).join('\n')
        : null,
      at: now()
    })
  })

  axios.interceptors.request.use((config) => {
    config.metadata = { start: Date.now() }
    return config
  })

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      const config = err.config || {}
      const start = config.metadata && config.metadata.start
      push(failedRequests, {
        url: stripQuery(config.url),
        method: (config.method || '').toUpperCase(),
        status: err.response ? err.response.status : 'NETWORK_ERROR',
        durationMs: start ? Date.now() - start : null,
        at: now()
      })
      return Promise.reject(err)
    }
  )
}

// result: 서버 응답 코드(-1, -2 ...) 또는 토큰. 토큰은 저장하지 않고 'success'로 치환
function recordLoginAttempt(method, id, result) {
  const value = String(result)
  const lastResult = value.length >= 10 ? 'success' : value
  const at = now()

  loginAttempt.method = method
  loginAttempt.id = id
  loginAttempt.count += 1
  loginAttempt.firstAt = loginAttempt.firstAt || at
  loginAttempt.lastAt = at
  loginAttempt.lastResult = lastResult
  loginAttempt.lastResultMessage = LOGIN_RESULT_MESSAGES[lastResult] || '기타 오류'
  loginAttempt.stage = 'login_api'
  loginAttempt.stageAt = at
}

function recordLoginStage(stage) {
  loginAttempt.stage = stage
  loginAttempt.stageAt = now()
}

function recordWebglProgress(value) {
  webglLoad.lastProgress = value
  webglLoad.lastProgressAt = now()
}

function recordWebglComplete() {
  webglLoad.completedAt = now()
  // 페이지 진입 시점부터 WebGL 로딩 완료까지
  webglLoad.durationMs = Math.round(performance.now())
}

function isStorageAvailable(storage) {
  try {
    const key = '__argong_storage_test__'
    storage.setItem(key, key)
    storage.removeItem(key)
    return true
  } catch {
    return false
  }
}

function probeWebgl(type) {
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext(type)
  if (!gl) return null

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
  const result = {
    vendor: gl.getParameter(
      debugInfo ? debugInfo.UNMASKED_VENDOR_WEBGL : gl.VENDOR
    ),
    renderer: gl.getParameter(
      debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER
    ),
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE)
  }

  // 브라우저 WebGL 컨텍스트 개수 제한이 있으므로 Unity 쪽에 영향 없게 바로 해제
  const lose = gl.getExtension('WEBGL_lose_context')
  if (lose) lose.loseContext()
  return result
}

function getWebglInfo() {
  if (webglInfoCache) return webglInfoCache

  let webgl2 = null
  let webgl1 = null
  try {
    webgl2 = probeWebgl('webgl2')
    webgl1 = webgl2 || probeWebgl('webgl')
  } catch {
    // 일부 환경에서 컨텍스트 생성 자체가 예외를 던짐
  }
  const gpu = webgl2 || webgl1 || {}

  webglInfoCache = {
    webgl1Supported: !!webgl1,
    webgl2Supported: !!webgl2,
    vendor: gpu.vendor || null,
    renderer: gpu.renderer || null,
    maxTextureSize: gpu.maxTextureSize || null,
    softwareRenderer: SOFTWARE_RENDERER_PATTERN.test(gpu.renderer || ''),
    webAssemblySupported: typeof WebAssembly === 'object',
    sharedArrayBufferSupported: typeof SharedArrayBuffer !== 'undefined',
    crossOriginIsolated: window.crossOriginIsolated === true
  }
  return webglInfoCache
}

function getPageTiming() {
  const [nav] = performance.getEntriesByType('navigation')
  // WebGL iframe은 다른 출처라 내부 리소스(.data/.wasm) 타이밍은 읽을 수 없고 iframe 문서만 확인 가능
  const iframes = performance
    .getEntriesByType('resource')
    .filter((entry) => entry.initiatorType === 'iframe')
    .map((entry) => ({
      url: stripQuery(entry.name),
      durationMs: Math.round(entry.duration)
    }))

  return {
    dnsMs: nav ? Math.round(nav.domainLookupEnd - nav.domainLookupStart) : null,
    connectMs: nav ? Math.round(nav.connectEnd - nav.connectStart) : null,
    ttfbMs: nav ? Math.round(nav.responseStart - nav.requestStart) : null,
    domContentLoadedMs: nav ? Math.round(nav.domContentLoadedEventEnd) : null,
    loadMs: nav ? Math.round(nav.loadEventEnd) : null,
    iframes,
    pageStayMs: Math.round(performance.now())
  }
}

async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SERVER_CHECK_TIMEOUT_MS)
  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

async function checkServer() {
  const result = {
    apiReachable: false,
    apiStatus: null,
    apiRttMs: null,
    // 기기 시계 - 서버 시계 (양수면 기기 시계가 빠름)
    clockSkewMs: null
  }

  // API 서버 도달 여부: 기존 점검 확인 엔드포인트 사용
  const apiStart = Date.now()
  try {
    const res = await fetchWithTimeout(
      `${process.env.VUE_APP_CONNECT}/server/maintenance`
    )
    result.apiReachable = true
    result.apiStatus = res.status
  } catch {
    result.apiStatus = 'NETWORK_ERROR'
  }
  result.apiRttMs = Date.now() - apiStart

  // 기기 시계 오차: 교차 출처 API의 Date 헤더는 CORS로 읽을 수 없어 같은 출처 응답 사용 (초 단위 정밀도)
  try {
    const start = Date.now()
    const res = await fetchWithTimeout(`${window.location.origin}/favicon.ico`, {
      method: 'HEAD',
      cache: 'no-store'
    })
    const end = Date.now()
    const serverTime = Date.parse(res.headers.get('date'))
    if (!Number.isNaN(serverTime)) {
      result.clockSkewMs = Math.round((start + end) / 2 - serverTime)
    }
  } catch {
    // 측정 실패 시 null 유지
  }

  return result
}

async function collectEnvInfo(extra = {}) {
  const connection = navigator.connection || {}
  const uaData = navigator.userAgentData
  const server = await checkServer()

  return {
    sessionId,
    collectedAt: now(),
    browser: {
      userAgent: navigator.userAgent,
      platform: uaData ? uaData.platform : navigator.platform,
      brands: uaData ? uaData.brands.map((b) => `${b.brand} ${b.version}`) : [],
      deviceType:
        'ontouchstart' in window || navigator.maxTouchPoints > 0
          ? 'Mobile'
          : 'Desktop',
      screen: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookieEnabled: navigator.cookieEnabled,
      localStorageAvailable: isStorageAvailable(window.localStorage),
      sessionStorageAvailable: isStorageAvailable(window.sessionStorage)
    },
    webgl: {
      ...getWebglInfo(),
      load: { ...webglLoad }
    },
    network: {
      url: `${window.location.origin}${window.location.pathname}`,
      referrer: stripQuery(document.referrer),
      online: navigator.onLine,
      effectiveType: connection.effectiveType || null,
      downlinkMbps: connection.downlink || null,
      rttMs: connection.rtt || null,
      buildVersion: process.env.VUE_APP_BUILD_VERSION || 'unknown',
      timing: getPageTiming(),
      ...server
    },
    loginAttempt: { ...loginAttempt },
    shownMessages: [...shownMessages],
    jsErrors: [...jsErrors],
    failedRequests: [...failedRequests],
    ...extra
  }
}

export {
  initEnvCollector,
  recordLoginAttempt,
  recordLoginStage,
  recordWebglProgress,
  recordWebglComplete,
  collectEnvInfo
}
